<template>
  <base-widget
    ref="widget"
    :style="heightStyles"
    scrollable
    :loading="loading && isEmpty"
    class="collateral overflow-hidden d-flex"
  >
    <template #heading>
      <slot name="heading">
        <!-- Heading -->
        <div class="d-flex justify-content-between align-items-center heading-container mb-4">
          <!-- Editable title -->
          <div
            v-if="canEditTitle && !isSpecialFolder"
            class="d-inline-flex justify-start flex-nowrap align-items-center"
            :class="{ 'non-editable-name-container': !editingTitle }"
          >
            <base-texteditable
              v-if="editingTitle"
              ref="editableTitle"
              v-model="localTitle"
              :default-value="'New Album'"
              :placeholder="localTitle"
              max-width="219px"
              is-heading
              @complete="changeFolderName"
              @blur="changeFolderName"
            />
            <div
              v-else
              class="non-editable-name"
              @click="startEditingTitle"
            >
              <p>
                {{ localTitle }}
              </p>
            </div>
            <base-text
              v-if="count > 0"
              class="ml-1"
              light
              style="padding-left:8px; color: #767676"
            >
              ({{ count }})
            </base-text>
          </div>
          <!-- "All" or "Shared" folder -->
          <template v-else-if="isSpecialFolder">
            <base-heading class="collateral__heading__title">
              {{ getSpecialTitle() }} {{ isShared ? `(${items.length})` : '' }}
            </base-heading>
          </template>
          <template v-else>
            <base-heading class="collateral__heading__title">
              {{ localTitle }}
            </base-heading>
          </template>
          <div class="d-flex">
            <base-popover>
              <template slot="header">
                <base-text
                  v-if="canEditPermissions"
                  class="d-flex justify-content-left popover-title"
                >
                  <b>{{ $t('folders.detail.permissions') }}</b>
                </base-text>
                <div v-else />
              </template>
              <template>
                <album-owner-indicator
                  no-text
                  :is-institution="owner_is_institution"
                  :profile-pic="owner_profile_pic"
                  :abbr="user.profile.institution.abbr"
                  :owner-full-name="owner_is_institution ? (user.profile.institution.name || '') : ''"
                />
              </template>
              <template slot="content">
                <div v-if="canEditPermissions">
                  <div class="d-flex justify-content-between">
                    <base-text class="permission-title">
                      {{ $t('folders.detail.public') }}
                    </base-text>
                    <base-switch
                      :value="publicAlbum"
                      :disabled="isFolderLoading"
                      @input="$emit('public-album', $event)"
                    />
                  </div>
                  <base-text class="permission-description">
                    {{ publicAlbum ? $t('folders.detail.anyoneViews') : $t('folders.detail.onlyYouView') }}
                  </base-text>
                  <div class="d-flex justify-content-between anyone-can-post">
                    <base-text class="permission-title">
                      {{ $t('folders.detail.anyonePosts') }}
                    </base-text>
                    <base-switch
                      :value="anyoneCanPost"
                      :disabled="isFolderLoading"
                      @input="$emit('anyone-can-post', $event)"
                    />
                  </div>
                  <base-text class="permission-description">
                    {{ anyoneCanPost
                      ? $t('folders.detail.anyonePostsDescription')
                      : $t('folders.detail.onlyYouPostDescription') }}
                  </base-text>
                </div>
                <album-owner-indicator
                  v-if="!isOwner"
                  :class="{'mt-4': canEditPermissions}"
                  :owner-full-name="`${owner_first_name} ${owner_last_name}`"
                  :is-institution="owner_is_institution"
                  :profile-pic="owner_profile_pic"
                  :abbr="user.profile.institution.abbr"
                />
              </template>
            </base-popover>
            <base-search
              v-if="!isShared"
              v-model="search"
              style="min-width: 280px;"
              icon-position="left"
            />
            <base-button
              v-if="!isSpecialFolder && hasPermission "
              class="mr-0 d-flex ml-3 upload-button"
              nowrap
              small
              @click="$emit('share-clicked')"
            >
              <base-icon
                icon="shareV2"
                class="mr-2"
                style="height: 15px; margin: 0 0 0 3px;"
              />
              <base-text size="s" style="color:#333">
                {{ $t('folders.detail.share') }}
              </base-text>
            </base-button>
            <base-button
              v-if="canUpload && currentFolderPk !== 'all'"
              inverse
              nowrap
              class="d-flex upload-button"
              style="border: none !important;"
              @click="$emit('upload-clicked')"
            >
              <base-icon
                icon="uploadV2white"
                style="margin: 0 6px 0 6px;"
              />
              &nbsp;
              <base-text
                light
                size="s"
              >
                {{ $t('folders.detail.upload') }}
              </base-text>
            </base-button>
          </div>
        </div>
      </slot>
    </template>
    <template #loading>
      <div class="row mt-4">
        <div
          v-for="num in 5"
          :key="`collateral_skeleton_${num}`"
          class="col-3 mb-4"
        >
          <base-skeleton>
            <div
              class="skeleton__item"
              style="width: 100%; height: 175px;"
            />
          </base-skeleton>
        </div>
      </div>
    </template>
    <template #default>
      <!-- Body -->
      <template v-if="!items.length">
        <base-text
          v-if="search.length"
          class="ml-2"
        >
          {{ $t('common.noResult') }} "{{ search }}"
        </base-text>
        <template v-else-if="isCustomerViewing">
          <empty title="Folder is empty">
            <span>{{ $t('folders.detail.empty') }}</span>
          </empty>
        </template>
        <template v-else>
          <empty>
            <span v-if="canUpload" class="text-click">
              <base-icon
                icon="image"
                style="margin: 20px;"
                class="icon-plus"
              />
              <br>
              <span class="text-click">
                No photos yet, {{ $t('folders.detail.upload') }}
              </span>
              {{ $t('folders.detail.uploadSuffix') }}
              <br>
              <base-button
                nowrap
                class="upload-button m-3"
                @click="$emit('upload-clicked')"
              >
                <base-icon
                  icon="uploadV2black"
                  style="margin: 0 6px 0 6px;"
                />
              &nbsp;
                <base-text
                  light
                  size="s"
                >
                  {{ $t('folders.detail.upload') }}
                </base-text>
              </base-button>
            </span>
            <span v-else />
          </empty>
        </template>
      </template>
      <div
        v-else
        ref="scrollable"
        v-scrollable="{
          heightThresholdAdjust: adjustThreshold
        }"
        class="mb-10"
        @reached-bottom="items.length < count && $emit('reached-bottom')"
      >
        <!-- @scroll="onScroll" -->
        <div class="row">
          <!-- Album has elements -->
          <div
            v-if="canUpload && privateCollateralEnabled"
            class="col-3 mb-4"
          >
            <add-image-tile
              @click="$emit('upload-clicked')"
            />
          </div>
          <div
            v-for="(item, index) in items"
            :key="`collateral_item_${index}`"
            class="col-4 col-md-3 mb-4"
            :class="{shared: isShared}"
          >
            <item
              :src="isVideo(item) ? item.thumb : item.image"
              :is-vid="isVideo(item)"
              :liked="item.liked !== null"
              :linked="linked"
              :can-like="linked"
              :can-delete="canDelete(item)"
              :can-hover="canHover(item)"
              @delete="onDeleteClicked(item)"
              @like="$emit('like-collateral', item.pk)"
              @selected-image="$emit('selected-image', index)"
              @unlike="$emit('unlike-collateral', item.pk, item.liked)"
            />
          </div>
          <template v-if="loading">
            <div
              v-for="num in 5"
              :key="`collateral_skeleton_${num}`"
              class="col-3 mb-4"
            >
              <base-skeleton>
                <div
                  class="skeleton__item"
                  style="width: 100%; height: 175px;"
                />
              </base-skeleton>
            </div>
          </template>
        </div>
      </div>
    </template>
  </base-widget>
</template>

<script>
import minHeightMixin from '@/components/mixins/minheight';
import { EventBus } from '@/event-bus';
import Item from './Item';

export default {
  name: 'ItemsGrid',
  components: { Item },
  mixins: [minHeightMixin],
  props: {
    title: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    currentFolder: {
      type: Object,
      default: () => {},
    },
    canUpload: {
      type: Boolean,
      default: false,
    },
    canHover: {
      type: Function,
      default: (item) => false,
    },
    canDelete: {
      type: Function,
      default: (item) => false,
    },
    count: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isShared: {
      type: Boolean,
      default: false,
    },
    isOwner: {
      type: Boolean,
      default: false,
    },
    linked: {
      type: Boolean,
      default: false,
    },
    publicAlbum: {
      type: Boolean,
      default: false,
    },
    anyoneCanPost: {
      type: Boolean,
      default: false,
    },
    hasPermission: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: '',
      editingTitle: false,
      localTitle: '',
      /**
       * `firstLoading` was removed in favor of `isEmpty` to check
       * if is first load or a new page
       */
      ref: 'widget',
    };
  },
  computed: {
    user() {
      return this.$store.state.account.user;
    },
    owner_first_name() {
      return this.currentFolder?.owner_first_name ?? this.institutionName;
    },
    owner_last_name() {
      return this.currentFolder?.owner_last_name ?? '';
    },
    owner_profile_pic() {
      return this.currentFolder?.owner_profile_pic ?? '';
    },
    owner_is_institution() {
      return this.currentFolder?.owner_is_institution ?? true;
    },
    isEmpty() {
      return this.items.length === 0;
    },
    isSpecialFolder() {
      return this.currentFolderPk === 'all' || this.isShared || this.currentFolder.sync_target !== null;
    },
    isFolderLoading() {
      return this.$store.state.folders.isLoading;
    },
    deleteMessage() {
      return this.$store.state.folders.item.shared
        ? 'Are you sure you\'d like to delete your Collage from the Shared Collages folder? It will be removed for you and your colleagues.'
        : 'This image will be deleted. Would you like to continue?';
    },
    currentFolderPk() {
      return this.$route.params.folderId;
    },
    institutionName() {
      return this.$store.state.account.user.profile.institution.name;
    },
    adjustThreshold() {
      /*
         raise infinite-scroll-triggering distance threshold fix for iPad!
         (bumped up from a default of 100, see src/_utilities/Scrollable.js:3  isInViewport()
         https://helloseer.monday.com/boards/1326835386/pulses/1334707024

         311 here is a somewhat arbitrary number David tested manually with an
         iPad and browser that seems to make the issue go away
       */
      return 311;
    },
    isCustomerViewing() {
      return this.$route.path.startsWith('/collateral/link');
    },
    privateCollateralEnabled() {
      return this.isCustomerViewing || this.useFeature('private_collateral');
    },
    shareable() {
      return this.privateCollateralEnabled && this.currentFolderPk && this.currentFolderPk !== 'shared' && this.currentFolderPk !== 'all';
    },
    isAdminMode() {
      return this.$store.state.account.isAdminMode;
    },
    canEditPermissions() {
      return this.isOwner || this.isAdminMode;
    },
    canEditTitle() {
      return this.isOwner || this.isAdminMode;
    },
  },
  watch: {
    search(newval) {
      this.$emit('search', newval);
    },
    title: {
      immediate: true,
      handler(newval) {
        this.localTitle = newval;
      },
    },
  },
  created() {
    this.hasPermission = true; // this.usePermission('album_sharing_privilege');
  },
  methods: {
    debug() {
      // eslint-disable-next-line
      console.log('Reached bottom');
    },
    onDeleteClicked(item) {
      EventBus.$emit('confirm', {
        message: this.deleteMessage,
        positiveText: 'Delete',
        callback: (confirm) => {
          if (confirm) {
            this.$emit('delete', item);
          }
        },
      });
    },
    startEditingTitle() {
      this.editingTitle = true;
      this.$nextTick(() => this.$refs.editableTitle.focus());
    },
    changeFolderName() {
      this.$emit('folder-name-change', this.localTitle);
      this.editingTitle = false;
    },
    getSpecialTitle() {
      if (this.currentFolderPk === 'all') {
        return 'All Photos';
      } if (this.currentFolderPk === 'shared') {
        return 'Shared Collages';
      }
      return this.localTitle;
    },
    isVideo(item) {
      return item.file_content_type === 'video/mp4';
    },
  },
};
</script>
<style lang="scss"
  scoped>
::v-deep .like-icon {
  transform: translateY(0);
}

.collateral {
  &::v-deep .heading {
    padding: 1.25rem 1.25rem 0;
    background-color: transparent;
  }

  &__heading__title {
    margin-bottom: 0;
    font-family: 'Tiempos';
    font-style: normal;
    font-weight: 600;
    font-size: 23px;
    line-height: 120%;
  }
}

.upload-button {
  border: 1px solid #C6C6C6;
  padding: 7px 5px;
}

::v-deep .shared {
  padding: 0 1px 0 0;
  margin-bottom: 1px !important;
}

.permission-title {
  color: $black;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  overflow: hidden;
  align-items: center;
}

.anyone-can-post {
  padding-top: 8px;
}

.permission-description {
  margin-top: 8px;
  margin-bottom: 8px;
  color: $text-muted-color;
  font-size: 10px;
  line-height: 16px;
}

.permission-avatar {
  margin-right: 4px;
}

.popover-title {
  color: #000000;
  border-bottom: solid 1px rgba(black, 0.2);
  padding-bottom: 12px;
  margin-bottom: 18px;
  text-align: center;
}

::v-deep .base-texteditable {
  border: 2px solid #D1B0A0 !important;
  height: 34px;
  padding-left: 7px;
  border-radius: 5px;
  font-family: $font-family-serif;
  font-style: normal;
  font-weight: 300 !important;
  font-size: 20px;
  line-height: 24px;
}

::v-deep .base-heading {
  cursor: default;
}

.non-editable-name-container {
  white-space: nowrap;
  overflow: hidden;
  width: 100%;

  & .non-editable-name {
    width: auto;
    max-width: calc(100% - 30px);
  }

  & p {
    font-family: $font-family-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    overflow: hidden;
    text-overflow: ellipsis !important;
    margin: 0;
  }
}

.heading-container {
  width: 100%;
  overflow: hidden;
}
.text-click{
  font-weight: 300;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #1B1B1B;
}
</style>
